/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject, useEffect, useState } from 'react'
import { Field, Form, Formik, FormikProps } from 'formik'
import styled from 'styled-components'
import QRCode from 'qrcode'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm, Loader } from '@nexigendigital/nxui'
import { useAddTwoFactorMutation, useCreateTwoFactorMutation } from 'api/account'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    form: RefObject<FormikProps<{ code: string }>>
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ConfigureAppAuthForm({ form }: Props) {
    const [dataURL, setDataURL] = useState('')
    const [, { isSuccess, data }] = useCreateTwoFactorMutation({ fixedCacheKey: 'create-two-factor-authenticator' })
    const [addTwoFactor] = useAddTwoFactorMutation({ fixedCacheKey: 'add-two-factor-authenticator' })

    /****************************************************
     *  FUNCTIONS
     ****************************************************/
    async function generateQR(url: string) {
        try {
            return await QRCode.toDataURL(url, { width: 320 }) as any
        } catch (error) {
            return error
        }
    }

    useEffect(() => {
        if (isSuccess && data) {
            const { url } = data
            generateQR(url).then((url) => setDataURL(url))
        }
    }, [isSuccess, data])

    return (
        <Formik
            innerRef={form}
            initialValues={{
                code: ''
            }}
            validationSchema={
                Yup.object({
                    code: Yup.string()
                        .required('Required')
                })
            }
            onSubmit={({ code }) => {
                if (data) {
                    const { qr_code: verCode, secret } = data
                    addTwoFactor({ type: 'authenticator', identifier: verCode, secret, code, is_primary: true })
                }
            }}
        >
            {({ isSubmitting }) => (
                <Form id={'configureAppAuthForm'}>
                    <Container>
                        {isSuccess
                            ? <img src={dataURL} alt="QR Code" />
                            : <Loader message={'Fetching QR Code...'} />
                        }
                        {data?.secret &&
                            <Secret>
                                <h3>Token:</h3>
                                <p>{data.secret}</p>
                            </Secret>
                        }
                    </Container>
                    <Field
                        id={'code'}
                        label={'Verification Code'}
                        name={'code'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                </Form>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Secret = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    
    > h3 {
        font-weight: 500;
    }
    
    > p {
        margin-left: 5px;
    }
`

const Container = styled.div`
    margin: 0 auto 1rem;
`
